import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";

import Error from "../components/Error";
import Loader from "../components/Loader";
import "../components/Loader/style.scss";

import {setFirstTimeToPage} from "../actions/customer";
import {CustomerContext} from "../context/CustomerContext";
import {
  setCustomerSlotDetails,
  getCustomerSlotDetails,
} from "../services/SessionStorage";
import {
  validateRUT,
  validateDNI,
  validateNID,
  validatePhoneNumber,
  validateEmail,
  parseFormInput,
  validateCPF,
} from "../services/Validator";
import {setTheme} from "../App";
import {setTimeZone} from "../services/Format/Date";
import {
  DEFAULT_THEME,
  DEFAULT_LANGUAGE,
  DEFAULT_CUSTOMER_TYPE,
  SOURCE_STRING_SEPARATOR,
  CUSTOMER_ID_SESSION_KEY,
  CUSTOMER_EMAIL_SESSION_KEY,
  BAD_REQUEST_ERROR_TITLE,
} from "../constants";
import {setUrlTotemId} from "../actions/global";
import {getStoreByNodeId} from "../services/store/store.service";

import config from "../../config";
const SELECT_STORE_ROUTE = "/customer/selectStore";
const SELECTION_ROUTE = "/customer/guestLandingPage";
const SELECT_SERVICE_ROUTE = "/customer/selectTurneroStoreAndType";
window.androidObj = function AndroidClass() {};
function getThemeName(source) {
  if (!source) return DEFAULT_THEME;
  return source.split(SOURCE_STRING_SEPARATOR)[0];
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function validateCustomerId(country, customerId) {
  let isValid = false;
  if (
    config.dni_enabled_country_list.includes(country) &&
    validateDNI(customerId)
  ) {
    isValid = true;
  } else if (
    config.rut_enabled_country_list.includes(country) &&
    validateRUT(customerId)
  ) {
    isValid = true;
  } else if (
    config.nationalId_enabled_country_list.includes(country) &&
    validateNID(customerId)
  ) {
    isValid = true;
  } else if (
    config.curp_enabled_country_list.includes(country) &&
    validatePhoneNumber(customerId)
  ) {
    isValid = true;
  } else if (
    config.cpf_enabled_country_list.includes(country) &&
    validateCPF(customerId)
  ) {
    isValid = true;
  }
  return isValid;
}

function processUrlData(
  country,
  source,
  customerId,
  email,
  isOffice,
  bu,
  origin
) {
  let processedData = {
    navigateRoute: origin !== "web" ? SELECT_SERVICE_ROUTE : SELECTION_ROUTE, //Default Route
    customerData: {},
  };
  const isCustomerIdValid = isOffice
    ? validateEmail(customerId)
    : validateCustomerId(country, customerId);
  if (config.email_enabled_source_list.includes(source)) {
    const isEmailValid = validateEmail(email);
    if (isCustomerIdValid && isEmailValid) {
      processedData.navigateRoute = SELECT_STORE_ROUTE;
      processedData.customerData[CUSTOMER_ID_SESSION_KEY] =
        parseFormInput.parseOnlyLetterAndNumbers(customerId.toLowerCase());
      processedData.customerData[CUSTOMER_EMAIL_SESSION_KEY] =
        email.toLowerCase();
    } else if (isEmailValid) {
      processedData.customerData[CUSTOMER_EMAIL_SESSION_KEY] = email;
    } else if (isCustomerIdValid) {
      processedData.customerData[CUSTOMER_ID_SESSION_KEY] = customerId;
    }
  } else {
    if (isCustomerIdValid) {
      processedData.customerData[CUSTOMER_EMAIL_SESSION_KEY] = email;
      processedData.customerData.isOffice = isOffice;
      if (isOffice) {
        processedData.navigateRoute = SELECT_STORE_ROUTE;
      } else if (config.turnero_enabled_bu_list.includes(bu)) {
        processedData.navigateRoute = SELECTION_ROUTE;
      } else {
        processedData.navigateRoute = SELECT_STORE_ROUTE;
      }
      processedData.customerData[CUSTOMER_ID_SESSION_KEY] = isOffice
        ? customerId
        : parseFormInput.parseOnlyLetterAndNumbers(customerId.toLowerCase());
    }
  }
  return processedData;
}

const Customer = (props) => {
  const [error, setError] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const query = useQuery();
  const {i18n} = useTranslation();
  const actions = useContext(CustomerContext)[1];

  const fTime = useSelector((state) => state.customerReducer.firstTime);

  /**
   * MANDATORY URL PARAMS
   * country=[chile/peru/argentina]&bu=[falabella/sodimac/tottus]&source=[falabella_android/falabella_ios/falabella_web/sodimac_ios/fazil_android]
   * OPTIONAL URL PARAMS
   * customer_id=[RUT/DNI]&email=[EMAIL_ID]
   */
  const CUSTOMER_ID = useRef(query.get("customer_id"));
  const COUNTRY = useRef(query.get("country"));
  const CUSTOMER_EMAIL = useRef(query.get("email"));
  const CUSTOMER_FIRST_NAME = useRef(query.get("f_name"));
  const CUSTOMER_LAST_NAME = useRef(query.get("l_name"));
  const CUSTOMER_PHONE_NUMBER_WITHOUT_CODE = useRef(query.get("phone_num"));
  const CUSTOMER_LOYALTY_POINTS = useRef(query.get("loyalty_points"));
  const BU = useRef(query.get("bu"));
  const SOURCE = useRef(
    BU.current === "falabella_web"
      ? "falabella_totem"
      : BU.current === "sodimac_web"
      ? "sodimac_totem"
      : "falabella_totem" // valor por defecto si no es ninguno de los anteriores
  );
  const MALLNAME = useRef(query.get("mall_name"));
  let isOffice = useRef(query.get("office"));
  const tempOfficeTheme = useRef(query.get("theme"));
  const urlStoreid = useRef(query.get("storeid"));
  const urlNodeId = useRef(query.get("nodeid") || query.get("nodeId"));
  const urlTotemid = useRef(query.get("totemid"));
  const urlOrigin = useRef(query.get("origin"));

  dispatch(setUrlTotemId(urlTotemid.current));
  const officeTheme =
    tempOfficeTheme.current !== null ? tempOfficeTheme.current : DEFAULT_THEME;
  isOffice = isOffice.current === null ? false : true;
  const CURRENT_THEME = useRef(null);

  const currentCountryCode = config.country_code_map[COUNTRY.current];

  const fetchStoreData = useCallback(
    async (userDetails) => {
      // const storeid = userDetails.mallname;
      const storeDetails = {};
      const customerSlotDetails = getCustomerSlotDetails();
      if (customerSlotDetails.selectedStore === undefined) {
        try {
          if (isSending) return;
          setIsSending(true);
          // let completedata = await getStore(storeid);

          const completedata = await getStoreByNodeId(urlNodeId);
          //const completedata = await processApiResponse(response); //! REFACTOR
          let sessionData = userDetails;
          storeDetails.serviceTypes = completedata.serviceTypes;
          storeDetails.storeName = completedata.storeName;
          storeDetails.uuid = completedata.uuid;
          storeDetails.timeZone = completedata.timeZone;
          storeDetails.noOfStoresAllowedMax = completedata.noOfStoresAllowedMax;
          storeDetails.allowCurrentSlotBooking =
            completedata.allowCurrentSlotBooking;
          storeDetails.termsCondition = completedata.termsCondition;
          storeDetails.location = completedata.location
            ? completedata.location
            : "";
          storeDetails.termsConditionsAdditional =
            completedata.termsConditionsAdditional;
          storeDetails.callCode = "+" + completedata.stdCode + " ";
          sessionData["storeStoreData"] = {selectedStore: storeDetails};
          sessionData["isOffice"] = false;
          const customerPhoneNumber = `${completedata.stdCode}${CUSTOMER_PHONE_NUMBER_WITHOUT_CODE.current}`;
          setCustomerSlotDetails({
            ...customerSlotDetails,
            ...sessionData,
            customerPhoneNumber,
          });
          setIsSending(false);
          if (urlOrigin.current !== "web") {
            navigate(SELECT_SERVICE_ROUTE, {replace: true});
            return;
          }
          navigate(SELECTION_ROUTE, {replace: true});
        } catch (error) {
          setIsSending(false);
          navigate("/");
          return false;
        } finally {
          /**
           * No details added here as it leads to memory leak as screen would be navigated to other screen
           */
        }
      }
    },
    [isSending, navigate]
  );

  useEffect(() => {
    if (isOffice) {
      BU.current = config.officeConfig.bu;
      COUNTRY.current = config.officeConfig.country;
      SOURCE.current = config.officeConfig.source;
    }
    if (
      !BU.current ||
      !COUNTRY.current ||
      !SOURCE.current ||
      !config.valid_source_list.includes(SOURCE.current)
    ) {
      setError(
        "Ha ocurrido un error al intentar procesar tu solicitud, intenta de nuevo mas tarde."
      );
    } else if (
      BU.current === "test" ||
      COUNTRY.current === "test" ||
      SOURCE.current === "test"
    ) {
      setTheme(DEFAULT_THEME);
      i18n.changeLanguage(DEFAULT_LANGUAGE);
      setTimeZone();
      const testCustomerSlotDetails = {
        customerType: DEFAULT_CUSTOMER_TYPE,
        country: config.app_test_config.country,
        bu: config.app_test_config.bu,
        source: config.app_test_config.source,
        theme: DEFAULT_THEME,
        buList: [config.app_test_config.bu],
        officeTheme: DEFAULT_THEME,
        customerEmail: CUSTOMER_EMAIL.current,
        customerFirstName: CUSTOMER_FIRST_NAME.current || "",
        customerLastName: CUSTOMER_LAST_NAME.current || "",
        customerPhoneNumberWithoutCode:
          CUSTOMER_PHONE_NUMBER_WITHOUT_CODE.current || null,
        customerLoyaltyPoints: CUSTOMER_LOYALTY_POINTS.current || 0,
      };
      setCustomerSlotDetails(testCustomerSlotDetails);
      navigate(SELECTION_ROUTE, {replace: true});
    } else {
      CURRENT_THEME.current = getThemeName(SOURCE.current);
      if (isOffice) {
        const themeName =
          officeTheme === DEFAULT_THEME
            ? CURRENT_THEME.current
            : CURRENT_THEME.current + "-" + officeTheme;
        setTheme(themeName);
      } else {
        setTheme(CURRENT_THEME.current);
      }
      const currentLanguage = config.country_language_map[COUNTRY.current];
      const currLanguage = currentLanguage
        ? currentLanguage
        : config.country_language_map["default"];
      i18n.changeLanguage(currLanguage);
      setTimeZone(currentCountryCode);
      const currentBU = config.bu_name_map[BU.current];
      let currentBUDisplayList;
      if (currentBU) {
        currentBUDisplayList = config.stand_alone_bu_list.includes(currentBU)
          ? [currentBU]
          : config.shared_platform_bu_list;
      } else {
        currentBUDisplayList = [];
      }

      let customerSlotDetails = {
        customerType: DEFAULT_CUSTOMER_TYPE,
        country: currentCountryCode,
        bu: currentBU,
        source: SOURCE.current,
        defaultStoreId: urlStoreid.current,
        nodeId: urlNodeId.current,
        origin: urlOrigin.current,
        theme: isOffice
          ? CURRENT_THEME.current + "-" + officeTheme
          : CURRENT_THEME.current,
        buList: currentBUDisplayList,
        mallname: MALLNAME.current,
        officeTheme: officeTheme,
        customerEmail: CUSTOMER_EMAIL.current,
        customerFirstName: CUSTOMER_FIRST_NAME.current || "",
        customerLastName: CUSTOMER_LAST_NAME.current || "",
        customerPhoneNumberWithoutCode:
          CUSTOMER_PHONE_NUMBER_WITHOUT_CODE.current || null,
        customerLoyaltyPoints: CUSTOMER_LOYALTY_POINTS.current || 0,
      };
      const {navigateRoute, customerData} = processUrlData(
        COUNTRY.current,
        SOURCE.current,
        CUSTOMER_ID.current,
        CUSTOMER_EMAIL.current,
        isOffice,
        BU.current,
        urlOrigin.current
      );
      if (!isOffice) {
        const index = config.id_alternatives[
          customerSlotDetails.country
        ].findIndex((val) => val.default === true);
        customerData["userIdType"] =
          config.id_alternatives[customerSlotDetails.country][index].value;
      }
      if (customerData.userId) {
        //Set userId in CustomerContext
        const {userId} = customerData;
        actions.setCustomerId({[CUSTOMER_ID_SESSION_KEY]: userId});
      }

      setCustomerSlotDetails({
        ...customerSlotDetails,
        ...customerData,
      });
      if (fTime) {
        dispatch(setFirstTimeToPage(false));
        if (customerData.userId) {
          if (isOffice) {
            // Commented to hide covid questions.
            // navigate("/customer/officeQuestions");
            navigate("/customer/selectStore");
          } else {
            navigate("/customer/loggedInLandingPage");
          }
        } else {
          if (config.stand_alone_bu_list.includes(currentBU)) {
            fetchStoreData(customerSlotDetails);
          } else {
            navigate(navigateRoute, {replace: true});
          }
        }
      } else {
        dispatch(setFirstTimeToPage(true));
        if (window.androidObj && window.androidObj.isBackPressed) {
          window.androidObj.isBackPressed(true);
        } else if (window.webkit && window.webkit.messageHandlers) {
          window.webkit.messageHandlers.isBackPressed.postMessage(true);
        } else {
          if (config.stand_alone_bu_list.includes(currentBU)) {
            fetchStoreData(customerSlotDetails);
          } else {
            navigate(navigateRoute, {replace: true});
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, i18n, navigate]);

  return !error ? (
    <div className="loader-container">
      <Loader />
    </div>
  ) : (
    <Error errorTitle={BAD_REQUEST_ERROR_TITLE} error={error} />
  );
};

export default Customer;
